import getIdToken from '@/helpers/firebase-token';
import axios from '@/helpers/axios-config';

export const WELCOME_OFFER_RELEASE_DATE = '2025-05-05 00:00:00';

const API_URL = process.env.VUE_APP_API_URL;
const isGloballyEnabled = process.env.VUE_APP_WELCOME_OFFER_ENABLED === 'true';

const MAX_TOKENS = 15;
const TOTAL_CASH_AVAILABLE = 20;

const SOCKET_MESSAGE_ACTIVATED = 'activated';
const SOCKET_MESSAGE_UPDATED = 'updated';

const LS_WELCOME_OFFER_POPUP = 'welcome_offer_popup_shown';

const state = {
  isEnabled: isGloballyEnabled,
  showWelcomeOfferPopup: false,
  isActive: null,
  isArchived: null,
  tokensCollected: 0,
  totalTokens: MAX_TOKENS,
  totalCashAvailable: TOTAL_CASH_AVAILABLE,
  notificationData: null,
  chestList: [],
};

const getters = {
  notificationsCount: (state) => (state.notificationData === null ? 0 : 1),

  localStorageKey: (s, g, rootState) => `${LS_WELCOME_OFFER_POPUP}_${rootState.playerInfo?.profile?.id}`,

  isCompleted: (state, getters) => getters.cashCollected === state.totalCashAvailable,

  cashCollected: (state) => {
    if (!state.chestList?.length) return 0;
    return state.chestList.reduce((acc, item) => acc + (item.claimed ? 1 : 0), 0);
  },
};

const actions = {
  setShowWelcomeOfferPopup({ commit }, value) {
    commit('SET_SHOW_WELCOME_OFFER_POPUP', value);
  },

  setNotificationData({ commit }, data) {
    commit('SET_NOTIFICATION_DATA', data);
  },

  clearNotificationData({ commit }) {
    commit('SET_NOTIFICATION_DATA', null);
  },

  async getWelcomeBonusSpots({ commit }) {
    const sessionKey = await getIdToken();
    const config = {
      method: 'post',
      url: `${API_URL}/bonuses/getWelcomeBonusSpots`,
      data: { sessionKey },
    };

    try {
      const { data } = await axios.request(config);

      if (data.hasError || !data.data) {
        throw new Error('Failed to get getWelcomeBonusSpots data');
      }

      const {
        active, // it means user has not deposited their first 5$
        archived, // it means that user is done with their bonuses and achievedSoFar is 20 (useless field tbh)
        spots, // means collected tokens
      } = data.data;

      commit('SET_ACTIVE', active || false);
      commit('SET_ARCHIVED', archived || false);
      commit('SET_TOKENS_COLLECTED', archived ? MAX_TOKENS : spots % MAX_TOKENS || 0);
    } catch (error) {
      console.error(error?.response?.data);
    }
  },

  async getWelcomeBonus({ commit }) {
    const sessionKey = await getIdToken();
    const config = {
      method: 'post',
      url: `${API_URL}/bonuses/getWelcomeBonus`,
      data: { sessionKey },
    };

    try {
      const { data } = await axios.request(config);

      if (data.hasError || !data.data) {
        throw new Error('Failed to get getWelcomeBonus data');
      }

      commit('SET_CHEST_LIST', data.data);
    } catch (error) {
      console.error(error?.response?.data);
    }
  },

  async syncWelcomeBonus({ state, dispatch }, message) {
    let title, text;

    await Promise.all([dispatch('getWelcomeBonusSpots'), dispatch('getWelcomeBonus')]);

    if (message === SOCKET_MESSAGE_ACTIVATED) {
      title = '🎁 You’ve got an offer!';
      text = 'Click here to find out more!';
    } else if (message === SOCKET_MESSAGE_UPDATED) {
      if (state.tokensCollected === 1) {
        title = '🔓 You just got your first token!';
        text = 'Fill the rest to claim your reward.';
      } else if (state.tokensCollected === state.totalTokens) {
        title = '🎁 Congratulations!';
        text = 'Click here to collect your reward.';
      } else {
        title = '🔓 You just got your token!';
        text = 'Fill the rest to claim your reward.';
      }
    }

    title && dispatch('setNotificationData', { title, text });
  },
};

const mutations = {
  SET_NOTIFICATION_DATA(state, data) {
    state.notificationData = data;
  },

  SET_ACTIVE(state, isActive) {
    state.isActive = isActive;
  },

  SET_ARCHIVED(state, isArchived) {
    state.isArchived = isArchived;
  },

  SET_TOKENS_COLLECTED(state, collectedTokens) {
    state.tokensCollected = collectedTokens;
  },

  SET_CHEST_LIST(state, list) {
    state.chestList = list;
  },

  SET_SHOW_WELCOME_OFFER_POPUP(state, value) {
    state.showWelcomeOfferPopup = value;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
